import { Add } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
const { ThemeProvider } = require('@emotion/react');
const { defaultTheme } = require('theme');

function TabularDataStepOne({
  open,
  onClose,
  onNext,
  isLoading,
  programList,
  initialGetData,
  isFromNotification,
}) {
  const [formData, setFormData] = useState({
    data_creation_date: '',
    table_description: '',
    source_id: '',
    source_name: '',
    data_refresh_date: '',
  });
  useEffect(() => {
    if (initialGetData) {
      if (initialGetData.source_id === null) {
        setShowNewSourceField(true);
      }
      setFormData(initialGetData);
    }
  }, [initialGetData]);

  const [showNewSourceField, setShowNewSourceField] = useState(false);

  const [errors, setErrors] = useState({
    dataCreationDate: '',
    tableDescription: '',
    source: '',
    newSource: '',
  });

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;

    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
      ...(field === 'source_id' && value !== 'add-new-source' ? { source_name: '' } : {}),
    }));

    if (field === 'source_id') {
      setShowNewSourceField(value === 'add-new-source');
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!formData.data_creation_date) {
      newErrors.dataCreationDate = 'Data creation date is required';
    }
    if (!formData.table_description) {
      newErrors.tableDescription = 'Table description is required';
    }
    if (!formData.source_id) {
      newErrors.source = 'Source is required';
    } else if (formData.source_id === 'add-new-source' && !formData.source_name) {
      newErrors.newSource = 'New source name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      data_creation_date: '',
      table_description: '',
      source_id: '',
      source_name: '',
      data_refresh_date: '',
    });
    setShowNewSourceField(false);
    setErrors({
      dataCreationDate: '',
      tableDescription: '',
      source: '',
      newSource: '',
    });
  };

  const handleNext = async () => {
    if (validateFields()) {
      await onNext(formData);
      resetForm();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      resetForm();
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <ThemeProvider theme={defaultTheme}>
      <Dialog
        open={open}
        onClose={() => {}}
        sx={{
          '.MuiDialog-paper': {
            width: '600px',
            boxShadow:
              '0px 11px 15px -7px rgba(0, 0, 0, 0.2),0px 24px 38px 3px rgba(0, 0, 0, 0.14),0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          className='text-heading-style-1'
          sx={{
            padding: '16px 24px',
            borderBottom: '1px solid rgba(0, 37, 71, 0.12)',
          }}
        >
          Step 1 of 2: Add table details
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 24px !important' }}>
          <div
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <TextField
              fullWidth
              id='outlined'
              type='date'
              required
              label='Data creation date'
              variant='outlined'
              color='secondary'
              className='outlined-textfield-style-1'
              value={formData.data_creation_date || ''}
              onChange={handleInputChange('data_creation_date')}
              error={!!errors.dataCreationDate}
              helperText={
                errors.dataCreationDate || 'The date your dataset was originally created'
              }
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  '& input::-webkit-calendar-picker-indicator': {
                    filter:
                      'invert(48%) sepia(12%) saturate(478%) hue-rotate(174deg) brightness(89%) contrast(85%)',
                    opacity: 1,
                    width: '18px',
                    height: '20px',
                    cursor: 'pointer',
                  },
                },
              }}
              inputProps={{
                max: today,
              }}
              FormHelperTextProps={{
                sx: { color: '#00254799' },
              }}
            />
            <TextField
              label='Table description'
              type='text'
              fullWidth
              multiline
              rows={3}
              required
              variant='outlined'
              color='secondary'
              className='outlined-textfield-style-1'
              value={formData.table_description || ''}
              onChange={handleInputChange('table_description')}
              error={!!errors.tableDescription}
              helperText={errors.tableDescription}
              InputLabelProps={{
                shrink: formData.table_description ? true : undefined,
              }}
            />
            <div>
              <TextField
                label='Source'
                select
                fullWidth
                required
                variant='outlined'
                color='secondary'
                value={formData.source_id || ''}
                className='textfield-select-style-1'
                helperText={
                  errors.source ||
                  'The original source of the data (e.g. Broward County Transit, FEMA, US Census, etc.)'
                }
                onChange={handleInputChange('source_id')}
                error={!!errors.source}
                FormHelperTextProps={{
                  sx: { color: '#00254799' },
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 3,
                      },
                    },
                  },
                }}
              >
                {programList &&
                  programList.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                      className='menu-item-container normal-text-style-2'
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                <MenuItem
                  key='add-new-source'
                  value='add-new-source'
                  sx={{ color: 'rgba(0, 37, 71, 0.87)' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Add /> <span>Add a new source</span>
                  </div>
                </MenuItem>
              </TextField>
            </div>
            {showNewSourceField && (
              <TextField
                label='New Source'
                type='text'
                fullWidth
                required={showNewSourceField}
                variant='outlined'
                color='secondary'
                className='outlined-textfield-style-1'
                helperText={errors.newSource || 'Official name of your new source'}
                FormHelperTextProps={{
                  sx: { color: '#00254799' },
                }}
                value={formData.source_name || ''}
                onChange={handleInputChange('source_name')}
                error={!!errors.newSource}
              />
            )}
            <TextField
              fullWidth
              id='outlined'
              type='date'
              label='Data refresh date'
              variant='outlined'
              color='secondary'
              className='outlined-textfield-style-1'
              helperText="You’ll get an 'Update your data' email reminder on this date."
              InputLabelProps={{
                shrink: true,
              }}
              value={formData.data_refresh_date || ''}
              onChange={handleInputChange('data_refresh_date')}
              InputProps={{
                sx: {
                  '& input::-webkit-calendar-picker-indicator': {
                    filter:
                      'invert(48%) sepia(12%) saturate(478%) hue-rotate(174deg) brightness(89%) contrast(85%)',
                    opacity: 1,
                    width: '18px',
                    height: '20px',
                    cursor: 'pointer',
                  },
                },
              }}
              inputProps={{
                min: today,
              }}
              FormHelperTextProps={{
                sx: { color: '#00254799' },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            className='primary-text-button-medium'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            className='primary-contained-button-medium'
            onClick={handleNext}
            disabled={isLoading}
            endIcon={isLoading ? <CircularProgress size={12} /> : null}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default TabularDataStepOne;
